import React from "react";
import Box from "../patterns/Box";
import Flex from "../patterns/Flex";
import Text from "../patterns/Text";
import { MessageRow } from "../messages/MessageRow";
import { getUserPhotoPlaceholderString } from "../messages/MessageRow";
import { ChatMessage, getStoryCharacters } from "../../pages/Create";
import { StoryCharacter } from "../messages/ChatInputField";
import { getChatSubtitle } from "../ChatHeader";
import { getChatMessageArrayFromFirebase } from "../../pages/Read";
import { db } from "../../utils/firebase";
import { doc, getDoc } from "firebase/firestore";
import { ChevronRightIcon } from "@primer/octicons-react";
import UserPhoto from "../patterns/UserPhoto";

interface DiscoverStoryCardProps {
  storyId: string;
  storyData: {
    title?: string;
    views?: number;
    totalLikes?: number;
    totalComments?: number;
    author?: string;
    characters?: Record<string, { isReceiver: boolean }>;
  };
  marginTop?: boolean;
}

export const DiscoverStoryCard = ({
  storyId,
  storyData,
  marginTop = false,
}: DiscoverStoryCardProps) => {
  const [storyCharacters, setCharacters] = React.useState<StoryCharacter[]>([]);
  const [featuredMessages, setFeaturedMessages] = React.useState<ChatMessage[]>(
    []
  );
  const [authorName, setAuthorName] = React.useState<string | null>(null);

  React.useEffect(() => {
    // Fetch characters
    if (storyData.characters && typeof storyData.characters === "object") {
      const charsToGet: { id: string; isReceiver: boolean }[] = [];
      Object.keys(storyData.characters).map((charKey: string) => {
        const char = storyData.characters![charKey];
        charsToGet.push({ id: charKey, isReceiver: char.isReceiver });
      });

      if (charsToGet.length > 0) {
        getStoryCharacters(charsToGet, storyId).then((characters) => {
          if (characters) setCharacters(characters);
        });
      }
    }

    // Fetch first few messages for preview
    getChatMessageArrayFromFirebase(storyId)
      .then((messages) => {
        setFeaturedMessages(messages.slice(0, 3));
      })
      .catch(() => {});

    // Fetch author name
    if (storyData.author) {
      getDoc(doc(db, "userProfiles", storyData.author)).then((snap) => {
        setAuthorName(snap.data()?.displayName || snap.data()?.name || null);
      });
    }
  }, [storyId]);

  return (
    <Box
      onClick={() => {
        window.open(`/read/${storyId}`, "_self");
      }}
      style={{
        background: "white",
        boxShadow:
          "rgba(0, 0, 0, 0.1) 0px 1px 3px 0px, rgba(0, 0, 0, 0.06) 0px 1px 2px 0px",
        borderRadius: 12,
        border: "1px solid lightgray",
        overflow: "hidden",
        marginTop: marginTop ? 12 : undefined,
        cursor: "pointer",
      }}
    >
      {/* Header: character avatars + title + character names */}
      <Flex
        style={{
          background: "rgba(0,0,0,0.03)",
          borderBottom: "1px solid rgba(0,0,0,0.05)",
          alignItems: "center",
          padding: 10,
        }}
      >
        {/* Character avatar stack */}
        {storyCharacters.length > 0 && (
          <Flex style={{ marginRight: 10, flexShrink: 0 }}>
            {storyCharacters.slice(0, 3).map((char, idx) => (
              <UserPhoto
                key={char.id}
                size={28}
                URL={char.photo}
                placeholderText={getUserPhotoPlaceholderString(char.name)}
                style={{
                  marginLeft: idx > 0 ? -8 : 0,
                  zIndex: 3 - idx,
                  border: "2px solid white",
                  borderRadius: 100,
                }}
              />
            ))}
          </Flex>
        )}
        <Flex style={{ flexDirection: "column", flex: 1, paddingRight: 10 }}>
          <Text>{storyData.title || "Untitled"}</Text>
          <Text variant="meta" style={{ marginTop: 4 }}>
            {getChatSubtitle(storyCharacters)}
          </Text>
        </Flex>
        <ChevronRightIcon />
      </Flex>

      {/* Message preview area */}
      <Box style={{ position: "relative" }}>
        <Box style={{ height: 140, overflow: "hidden", padding: 10 }}>
          {featuredMessages.length > 0 &&
            featuredMessages.map((message, idx) => (
              <MessageRow
                renderReaderView
                key={`message-${idx}`}
                hideButtons
                character={message.character}
                showPhoto={
                  message.character &&
                  !(
                    featuredMessages.length > idx + 1 &&
                    message.character.id ===
                      featuredMessages[idx + 1].character?.id
                  )
                }
                showName={
                  idx === 0 ||
                  (idx - 1 >= 0 &&
                    message?.character?.id !==
                      featuredMessages[idx - 1]?.character?.id)
                }
                renderAsImageMessage={message.renderAsImageMessage}
                didLikeMessage={() => {}}
              >
                {message.message}
              </MessageRow>
            ))}
        </Box>
        <Box
          style={{
            position: "absolute",
            bottom: 0,
            left: 0,
            height: 80,
            width: "100%",
            background: "linear-gradient(transparent, white)",
          }}
        />
      </Box>

      {/* Footer: stats + author */}
      <Flex
        style={{
          background: "rgba(0,0,0,0.03)",
          borderTop: "1px solid rgba(0,0,0,0.05)",
          alignItems: "center",
          justifyContent: "space-between",
          padding: 10,
        }}
      >
        <Text variant="meta">
          {storyData.views || 0} {(storyData.views || 0) === 1 ? "view" : "views"} ·{" "}
          {storyData.totalLikes || 0}{" "}
          {(storyData.totalLikes || 0) === 1 ? "like" : "likes"}
        </Text>
        {authorName && (
          <Text variant="meta" style={{ fontWeight: 500 }}>
            by {authorName}
          </Text>
        )}
      </Flex>
    </Box>
  );
};

export default DiscoverStoryCard;
